import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerTable } from "../../table/tableFeatures";
import { GOALS } from "../../core/blocks/AdPlan/constants";
import useCategories from "../../../hooks/useCategories";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { FaPause, FaPlay } from "react-icons/fa6";
import { LuMoveUpRight } from "react-icons/lu";
import { Link } from "@tanstack/react-router";
import { css, cx } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { useFetch } from "../../../hooks/api";
import { useAppSelector } from "../../../redux/hooks";
import api from "../../../utils/api";
import { Accordion } from "../../core/basic/Accordion";
import { Button, buttonRecipe } from "../../core/basic/Button";
import { ButtonGroup } from "../../core/basic/ButtonGroup";
import Card from "../../core/basic/Card";
import Loading from "../../core/blocks/Loading";
import { Checkbox } from "../../fields/base/Checkbox";
import { RadioGroup } from "../../fields/base/RadioGroup";
import { TextAreaField } from "../../fields/base/TextAreaField";
import { SearchInput } from "../../fields/base/TextField";
import { Table } from "../../table/Table";
import ProductImage from "../category/listings/ProductImage";
import CategoryFilter from "./CategorySelector";
import { ExplainerBox } from "../../core/basic/ExplainerBox";
import { selectionStateToSelectedIds } from "../../table/utils";
import { Modal } from "../../core/basic/Modal";
import { useSimpleCan } from "../../../config/casl/casl";

// The API returns duplicate products when searching by "has_ad". Kept at module scope and pure:
// an inline or mutating select re-runs every render, handing the table a new `results` array.
const selectUniqueProducts = (response) => {
  const ids = new Set();
  return {
    ...response,
    data: {
      ...response.data,
      results: response.data.results.filter(({ id }) => !ids.has(id) && ids.add(id)),
    },
  };
};

const normalizeChannel = (channel) => {
  switch (channel.toLowerCase()) {
    case "amazon":
      return "AMZ";
    default:
      return channel;
  }
};

function MoveToDialog(props) {
  const { t } = useTranslation(["merchandising"]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleMove = useCallback(() => {
    props.actionMutation.mutate({
      name: "moveTo",
      category: { id: selectedCategory },
      selection: props.selection,
      allSelected: props.allSelected,
    });
    props.onClose();
  }, [props, selectedCategory]);

  return (
    <Modal
      isOpen={props.isOpen}
      onOpenChange={(isOpen) => !isOpen && props.onClose()}
      css={{ width: "40vw" }}
      title={t(($) => $.merchandising["Move to..."])}
    >
      <VStack alignItems={"stretch"} padding="large" overflow="hidden">
        <CategoryFilter currentCategory={selectedCategory} onCategorySelected={setSelectedCategory} />
        <Button alignSelf="center" isDisabled={!selectedCategory} onPress={handleMove}>
          {t(($) => $.merchandising["Move"])}
        </Button>
      </VStack>
    </Modal>
  );
}

const ProductListing = ({ currentCategory }) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const channel = useAppSelector((state) => state.channel);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const userCanUpdateProducts = useSimpleCan("update", "Product");

  const [searchQuery, setSearchQuery] = useState("");
  const [matchOn, setMatchOn] = useState("icontains");
  const [searchFields, setSearchFields] = useState({
    sku: true,
    asin: true,
    product_title: true,
    has_an_ad: "all",
    parent_asin: true,
  });
  // Cursor used for database pagination.
  const [cursor, setCursor] = useState(null);
  const [selection, setSelection] = useState({});
  const selectedIds = selectionStateToSelectedIds(selection);
  const [searchAsins, setSearchAsins] = useState("");
  const debounceSearchAsins = useDebounce(searchAsins, 500);
  const asinArray = useMemo(() => debounceSearchAsins?.split(/[,\s\n]+/).filter((n) => n), [debounceSearchAsins]);
  const [allSelected, setAllSelected] = useState(false);
  const [isOpenMoveToDialog, setIsOpenMoveToDialog] = useState(false);
  const topOfListingElement = useRef(null);

  // Track walmart vendor accounts for alternate category links since merch dashboard is unsupported without total sales data
  const orgIsWalmartVendor = channel.currentChannel === "walmart" && user.organization_type === "vendor";

  const { categories, currentCategoryDetails } = useCategories(currentCategory, "merchandising");

  // Pack all of our search filters into a consistent hash, meant to
  // be sent as query arguments to calls to /api/search/products.
  const filterArguments = useMemo(() => {
    const args = {
      query: searchQuery,
      category: currentCategory,
      matchOn: matchOn,
      search_fields: Object.keys(searchFields)
        .filter((k) => searchFields[k])
        .join(","),
      channel: normalizeChannel(channel.currentChannel),
      marketplace: marketPlace.marketPlace,
      has_an_ad: searchFields.has_an_ad === "all" ? null : searchFields.has_an_ad === "true",
      asins: asinArray?.filter((n) => n)?.join(","),
    };

    return args;
  }, [searchQuery, matchOn, channel, marketPlace, searchFields, currentCategory, asinArray]);

  const queryClient = useQueryClient();

  const productSearchQuery = useFetch(
    ["searchProducts", filterArguments, cursor],
    "/api/v2/products/",
    {
      ...filterArguments,
      cursor: cursor,
      expand: "advertisements",
    },
    {
      retry: 0,
      placeholderData: keepPreviousData,
      select: selectUniqueProducts,
    },
  );

  const actionMutation = useMutation({
    mutationFn: async (data) => {
      return api.post("/api/v2/products/", data, filterArguments);
    },
    onSuccess: async () => {
      setCursor(null);
      setSelection({});
      setAllSelected(false);
      setSearchAsins("");
      await queryClient.invalidateQueries({
        queryKey: ["searchProducts"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["searchCategories"],
      });
    },
  });

  // Handle moving to the next page of results.
  const handleNext = useCallback(() => {
    const next = new URL(productSearchQuery.data.data.next);
    if (!next) return;
    if (topOfListingElement.current) {
      topOfListingElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setCursor(next.searchParams.get("cursor"));
  }, [productSearchQuery.data]);

  // Handle moving to the previous page of results.
  const handlePrevious = useCallback(() => {
    const previous = new URL(productSearchQuery.data.data.previous);
    if (!previous) return;
    if (topOfListingElement.current) {
      topOfListingElement.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setCursor(previous.searchParams.get("cursor"));
  }, [productSearchQuery.data]);

  // If the data changed, erase the current cursor used for pagination.
  useEffect(() => {
    setCursor(null);
  }, [currentCategory, channel, marketPlace]);

  const columns = useMemo(() => {
    const columns = [
      {
        header: "",
        accessorKey: "id",
        enableResizing: false,
        size: 70,
        cell: ({ getValue }) => {
          return <ProductImage id={getValue()} divId={getValue()} width="40px" height="40px" />;
        },
        meta: {
          justify: "center",
        },
      },
      {
        accessorKey: "sku",
        header: function Header() {
          const { t: tt } = useTranslation(["merchandising"]);
          return tt(($) => $.merchandising["SKU"]);
        },
      },
      {
        accessorKey: "asin",
        header: function Header() {
          const { t: tt } = useTranslation(["merchandising"]);
          const currentChannel = useAppSelector((state) => state.channel.currentChannel);
          return currentChannel === "amazon" ?
              tt(($) => $.merchandising["ASIN"])
            : tt(($) => $.merchandising["Item ID"]);
        },
      },
      {
        accessorKey: "title",
        size: 450,
        header: function Header() {
          const { t: tt } = useTranslation(["merchandising"]);
          return tt(($) => $.merchandising["Title"]);
        },
        cell: ({ getValue, row }) => {
          const managedAds = row.original.advertisements?.filter(
            (ad) =>
              ad.ad_goal !== GOALS.IMPORTED_ADS.value ||
              (ad.ad_goal === GOALS.IMPORTED_ADS.value && ad.non_trellis_enrolled_ad_start_date),
          );
          const unmanagedAds = row.original.advertisements?.filter(
            (ad) => ad.ad_goal === GOALS.IMPORTED_ADS.value && !ad.non_trellis_enrolled_ad_start_date,
          );

          return (
            <VStack gap="xsmall">
              {getValue()}
              <Wrap gap="xsmall" justify="start" width="100%">
                {managedAds?.map((v, i) => {
                  let AdIcon = null;
                  let ad_label = t(($) => $.merchandising["Ad Active"]);
                  let ad_color = "primary";

                  if (i === 10) {
                    return (
                      <span className="d-block p-2" key={v.id}>
                        {t(($) => $.merchandising["+{count} more managed media plans"], {
                          count: managedAds?.length - (i + 1),
                        })}
                      </span>
                    );
                  }

                  if (i > 10) {
                    return null;
                  }

                  if ([1, 22, 2].includes(v.ad_status)) {
                    AdIcon = FaPlay;
                    ad_color = "active";
                    ad_label = t(($) => $.merchandising["Active"]);
                  } else if (v.ad_status === 3) {
                    AdIcon = FaPause;
                    ad_color = "paused";
                    ad_label = t(($) => $.merchandising["Paused"]);
                  } else if (v.ad_status === 4) {
                    AdIcon = FaPause;
                    ad_color = "paused";
                    ad_label = t(($) => $.merchandising["Archived"]);
                  } else {
                    return null;
                  }
                  return (
                    <Link
                      key={v.id}
                      to="/user/advertising/dashboard/ads/$id"
                      params={{ id: String(v.id) }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css({
                        maxWidth: "230px",
                        backgroundColor: ad_color === "active" ? "trellisPalette.12/30" : "trellisPalette.16/30",
                        borderRadius: "full",
                        display: "inline-flex",
                        alignItems: "center",
                        height: "24px",
                        justifyContent: "start",
                        fontSize: "xsmall",
                        paddingY: "xsmall",
                        paddingX: "small",
                        gap: "xsmall",
                        textDecoration: "none",
                        _hover: {
                          backgroundColor: ad_color === "active" ? "trellisPalette.12/50" : "trellisPalette.16/50",
                        },
                      })}
                    >
                      <AdIcon />
                      <span
                        className={css({
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        {t(($) => $.merchandising["{name} ({adLabel})"], { name: v.name, adLabel: ad_label })}
                      </span>
                    </Link>
                  );
                })}
                {unmanagedAds?.length ?
                  <span className="d-block p-2">
                    {managedAds?.length ?
                      t(($) => $.merchandising["+{count} media plans not managed by Trellis"], {
                        count: unmanagedAds?.length,
                      })
                    : <div
                        className={css({
                          maxWidth: "230px",
                          borderRadius: "full",
                          display: "inline-flex",
                          alignItems: "center",
                          height: "24px",
                          justifyContent: "start",
                          fontSize: "xsmall",
                          paddingY: "xsmall",
                          paddingX: "small",
                          boxSizing: "border-box",
                          gap: "xsmall",
                          textDecoration: "none",
                        })}
                      >
                        {t(($) => $.merchandising["{count} media plans not managed by Trellis"], {
                          count: unmanagedAds?.length,
                        })}
                      </div>
                    }
                  </span>
                : null}
              </Wrap>
            </VStack>
          );
        },
      },
    ];

    if (!currentCategory) {
      columns.push({
        accessorKey: "category",
        header: function Header() {
          const { t: tt } = useTranslation(["merchandising"]);
          return tt(($) => $.merchandising["Category"]);
        },
        cell: ({ getValue }) => {
          const productCategoryName = categories?.find(
            (category) => category.id === parseInt(getValue()),
          )?.leaf_category_name;

          return (
            <div className="text-center">
              <Link
                to={
                  orgIsWalmartVendor ?
                    "/user/advertising/dashboard/category/$id"
                  : "/user/merchandising/category/$category"
                }
                params={{ id: String(getValue()), category: String(getValue()) }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {productCategoryName}
              </Link>
            </div>
          );
        },
      });
    }
    return columns;
  }, [currentCategory, categories, orgIsWalmartVendor, t]);

  const categoryName = currentCategory ? currentCategoryDetails.name : t(($) => $.merchandising["All Products"]);

  const table = useServerTable({
    columns,
    data: productSearchQuery.data?.data?.results,
    rowCount: undefined, // cursor-paginated, no total
    enableSorting: false,
    enableRowSelection: userCanUpdateProducts,
    state: {
      rowSelection: selection,
    },
    onRowSelectionChange: setSelection,
    getRowId: (row) => row.id,
    meta: {
      showSkeleton: productSearchQuery.isFetching && (!productSearchQuery.data || productSearchQuery.isPlaceholderData),
    },
  });

  const hasPages = productSearchQuery.data?.data?.next || productSearchQuery.data?.data?.previous;

  if (actionMutation.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Card>
        <VStack alignItems="stretch" ref={topOfListingElement}>
          <Wrap>
            <p className={css({ margin: 0 })}>
              {t(($) => $.merchandising["Products - {category}"], { category: categoryName })}
            </p>
            {currentCategory ?
              <Link
                to="/user/merchandising/category/$category"
                params={{ category: String(currentCategory) }}
                className={cx(buttonRecipe({ variant: "link" }), css({ fontSize: "small" }))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(($) => $.merchandising["Visit Category"])}
              </Link>
            : null}
          </Wrap>

          <SearchInput
            label={t(($) => $.merchandising["Search Products"])}
            value={searchQuery}
            placeholder={t(($) => $.merchandising["Query..."])}
            onChange={setSearchQuery}
          />

          <Accordion title={t(($) => $.merchandising["Advanced"])}>
            <VStack alignItems="stretch" gap="xsmall">
              <Wrap gap="xlarge" justify="space-between">
                <RadioGroup
                  className={css({
                    maxWidth: "50%",
                  })}
                  orientation={"horizontal"}
                  label={t(($) => $.merchandising["Match On"])}
                  items={[
                    {
                      value: "icontains",
                      label: t(($) => $.merchandising["Contains"]),
                    },
                    {
                      value: "istartswith",
                      label: t(($) => $.merchandising["Starts With"]),
                    },
                    {
                      value: "iendswith",
                      label: t(($) => $.merchandising["Ends With"]),
                    },
                    {
                      value: "iregex",
                      label: t(($) => $.merchandising["Regex"]),
                    },
                  ]}
                  value={matchOn}
                  onChange={setMatchOn}
                />
                <div>
                  <label
                    className={css({
                      display: "flex",
                      gap: "xsmall",
                      color: "text.label",
                      fontSize: "medium",
                      fontWeight: "bold",
                      flexWrap: "nowrap",
                    })}
                  >
                    {t(($) => $.merchandising["Fields"])}
                  </label>
                  <HStack justify="space-around" maxWidth="50%">
                    <Checkbox
                      value={searchFields.product_title}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          product_title: value,
                        }))
                      }
                      label={t(($) => $.merchandising["Title"])}
                    />
                    <Checkbox
                      value={searchFields.sku}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          sku: value,
                        }))
                      }
                      label={t(($) => $.merchandising["SKU"])}
                    />
                    <Checkbox
                      value={searchFields.asin}
                      onChange={(value) =>
                        setSearchFields((old) => ({
                          ...old,
                          asin: value,
                        }))
                      }
                      label={
                        channel.currentChannel === "walmart" ?
                          t(($) => $.merchandising["Item ID"])
                        : t(($) => $.merchandising["ASIN"])
                      }
                    />
                    {channel.currentChannel === "amazon" && (
                      <Checkbox
                        value={searchFields.parent_asin}
                        onChange={(value) =>
                          setSearchFields((old) => ({
                            ...old,
                            parent_asin: value,
                          }))
                        }
                        label={t(($) => $.merchandising["Parent ASIN"])}
                      />
                    )}
                  </HStack>
                </div>
              </Wrap>
              <TextAreaField
                label={
                  channel.currentChannel === "walmart" ?
                    t(($) => $.merchandising["Search Item IDs"])
                  : t(($) => $.merchandising["Search ASINs"])
                }
                placeholder={
                  channel.currentChannel === "walmart" ?
                    t(($) => $.merchandising["Copy and paste Item IDs"])
                  : t(($) => $.merchandising["Copy and paste ASINs or SKUs"])
                }
                value={searchAsins}
                onChange={setSearchAsins}
              />
              <Button alignSelf="end" variant="secondary" onPress={() => setSearchAsins("")}>
                {t(($) => $.common.actions.Clear)}
              </Button>
            </VStack>
          </Accordion>
        </VStack>
      </Card>
      <HStack justify="space-between">
        {userCanUpdateProducts && (
          <>
            <Button
              isDisabled={selectedIds.length === 0}
              onPress={() => {
                setIsOpenMoveToDialog(true);
              }}
            >
              <LuMoveUpRight /> {t(($) => $.merchandising["Move to"])}
            </Button>
            <MoveToDialog
              isOpen={isOpenMoveToDialog}
              onClose={() => setIsOpenMoveToDialog(false)}
              actionMutation={actionMutation}
              allSelected={allSelected}
              selection={selectedIds}
            />
          </>
        )}
        <ButtonGroup
          buttons={[
            {
              value: "all",
              label: t(($) => $.merchandising["All Products"]),
            },
            {
              value: "true",
              label: t(($) => $.merchandising["Has Ad"]),
            },
            {
              value: "false",
              label: t(($) => $.merchandising["Has No Ad"]),
            },
          ]}
          value={searchFields.has_an_ad}
          onSelect={(newValue) =>
            setSearchFields({
              ...searchFields,
              has_an_ad: newValue,
            })
          }
        />
      </HStack>
      <Card>
        {hasPages && (table.getIsAllPageRowsSelected() || allSelected) ?
          <ExplainerBox>
            {allSelected ?
              <Trans
                t={t}
                i18nKey={($) =>
                  $.merchandising["All products matching this query selected. <clearLink>Clear selection</clearLink>."]
                }
                components={{
                  clearLink: (
                    <Button css={{ display: "contents" }} variant="link" onPress={() => setAllSelected(false)} />
                  ),
                }}
              />
            : <Trans
                t={t}
                i18nKey={($) =>
                  $.merchandising[
                    "All {selectedCount} products on this page are selected. Select <selectAllLink>all products</selectAllLink> that matched your query instead?"
                  ]
                }
                values={{ selectedCount: selectedIds.length > 25 ? 25 : selectedIds.length }}
                components={{
                  selectAllLink: (
                    <Button css={{ display: "contents" }} variant="link" onPress={() => setAllSelected(true)} />
                  ),
                }}
              />
            }
          </ExplainerBox>
        : null}
        <Table table={table} />
        {hasPages ?
          <HStack justify="end" paddingY={"xlarge"}>
            <Button
              variant={"secondary"}
              onPress={handlePrevious}
              isDisabled={!productSearchQuery.data?.data?.previous || productSearchQuery.isPlaceholderData}
            >
              {t(($) => $.merchandising["Previous"])}
            </Button>
            <Button
              variant={"secondary"}
              onPress={handleNext}
              isDisabled={!productSearchQuery.data?.data?.next || productSearchQuery.isPlaceholderData}
            >
              {t(($) => $.merchandising["Next"])}
            </Button>
          </HStack>
        : null}
      </Card>
    </>
  );
};

export default ProductListing;
